import store from '../store'
import router from '@/router'
import axios from '@/config/api'
const campaignType = process.env.VUE_APP_CAMPAIGN_TYPE ?? 'BNN'

// The campaign API answers a campaign the visitor may not open
// directly (e.g. message 'member_link_required') with a redirect target
// instead of a campaign: { campaign_id, slug, type, url }. Returns how
// to get there — { slug } to stay in the SPA, { url } to hand off — or
// null when there is nothing actionable, so the caller falls through to
// its usual error handling.
const getAutoRedirect = payload => {
  const redirect = payload?.data?.redirect
  const str = value => (typeof value === 'string' ? value.trim() : '')
  const slug = str(redirect?.slug)
  const url = str(redirect?.url)
  const type = str(redirect?.type).toUpperCase()
  const currentSlug = router?.history?.current?.params?.slug || ''

  // The target is the route we are already on: redirecting there would
  // reload or bounce in place.
  if (slug && slug === currentSlug) {
    return null
  }

  const isThisCampaignType =
    !!type && type === String(campaignType).toUpperCase()

  // Stay in the SPA only for a campaign this build serves on this host.
  if (slug && isThisCampaignType) {
    return { slug }
  }

  return url ? { url } : null
}

// Send the visitor to the redirect target: the ProductDetail route for
// an in-app campaign, otherwise its absolute url, the same way the
// views handle campaign.data.redirect_url.
const autoRedirect = ({ slug, url }) => {
  if (!slug) {
    window.location.href = url

    return false
  }

  const current = router?.history?.current
  const target = {
    name: 'ProductDetail',
    params: { slug },
    // Carry the query forward the way the global beforeEach guard
    // would, so a full page load below doesn't silently drop it.
    query: {
      ...current?.query,
      member_request_key: undefined,
    },
  }

  // Only the :slug param differs, so a router navigation would reuse
  // the mounted view: no hook re-runs and the old campaign stays on
  // screen. Load the target for real instead, which also clears the
  // slug-derived store state (campaign, model, cart, branch, user).
  if (current?.name === target.name) {
    window.location.assign(router.resolve(target).href)

    return false
  }

  return router
    .replace(target)
    .then(() => false)
    .catch(() => false)
}

export default async campaign => {
  if (campaign?.data?.status === 'active') {
    return true
  }

  const slug = router?.history?.current?.params?.slug || ''
  const { code } = router?.history?.current?.query
  const params = code ? `?code=${code}` : ''

  try {
    const result = !slug
      ? null
      : await axios.get(`/campaign/${campaignType}/${slug}${params}`)

    const val = result?.data || null

    if (val?.data?.id) {
      val.data.isHome = val?.data?.is_delivery_home
      val.data.isShop = val?.data?.is_delivery_store
      val.data.slug = slug
      store.dispatch('campaign/update', val)
      store.commit('campaign/updateLoading', false)
    }

    if (val?.data?.status !== 'active') {
      localStorage.removeItem('slug')
    }

    const redirect = getAutoRedirect(val)

    if (redirect) {
      return autoRedirect(redirect)
    }

    if (val?.status === 404) {
      return router
        .push({ name: 'Error', params: { error: 'notfound' } })
        .then(() => false)
        .catch(() => false)
    } else if (
      val?.data?.status === 'incoming' &&
      router.name !== 'Error'
    ) {
      localStorage.setItem('inComingRoute', val?.data?.slug)

      if (
        router?.name === 'CustomerInformation' ||
        router?.history?.current?.name === 'CustomerInformation'
      ) {
        return false
      }

      return router
        .push({
          name: 'CountDown',
          params: {
            slug: val?.data?.slug || this.slug,
          },
        })
        .then(() => false)
        .catch(() => false)
    } else if (
      val?.data?.status === 'expired' &&
      router.name !== 'Error'
    ) {
      return router
        .push({ name: 'Error', params: { error: 'expired' } })
        .then(() => false)
        .catch(() => false)
    } else if (val?.data?.status === 'active') {
      localStorage.setItem('slug', val?.data?.slug)

      if (
        router?.name === 'CustomerInformation' ||
        router?.history?.current?.name === 'CustomerInformation'
      ) {
        return false
      }

      return true
    } else {
      localStorage.removeItem('slug')

      return router
        .push({ name: 'Error', params: { error: 'notfound' } })
        .then(() => false)
        .catch(() => false)
    }
  } catch (e) {
    localStorage.removeItem('slug')

    const redirect = getAutoRedirect(e?.response?.data)

    if (redirect) {
      return autoRedirect(redirect)
    }

    return router
      .push({ name: 'Error', params: { error: 'notfound' } })
      .then(() => false)
      .catch(() => false)
  }
}
